import { useEffect, useRef, useState } from 'react';
import { IonSegment, IonSegmentButton, IonLabel, IonTextarea } from '@ionic/react';
import { MarkdownContent } from './MarkdownContent';
import { MarkdownToolbar } from './MarkdownToolbar';
import { MarkdownHelpButton } from './MarkdownHelpButton';
import {
  applyMarkdownFormat,
  continueListOnEnter,
  type MarkdownAction,
} from './markdownFormatting';
import { cn } from '../../lib/utils';

type EditorSegment = 'write' | 'preview';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
}

export const MarkdownEditor = ({
  value,
  onChange,
  placeholder,
  maxLength,
  disabled,
}: MarkdownEditorProps) => {
  const [segment, setSegment] = useState<EditorSegment>('write');
  const textareaRef = useRef<HTMLIonTextareaElement>(null);
  // Keep the latest onChange so the native keydown listener never closes over a stale one.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Auto-continue markdown lists on Enter. We listen on the underlying
  // <textarea> because getInputElement() is async and preventDefault() must be synchronous.
  useEffect(() => {
    if (segment !== 'write') return;

    let native: HTMLTextAreaElement | null = null;
    let cancelled = false;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.shiftKey) return;
      const el = event.currentTarget as HTMLTextAreaElement;
      const result = continueListOnEnter(el.value, el.selectionStart ?? 0, el.selectionEnd ?? 0);
      if (!result) return;
      event.preventDefault();
      onChangeRef.current(result.text);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(result.selectionStart, result.selectionEnd);
      });
    };

    textareaRef.current?.getInputElement().then((el) => {
      if (cancelled || !el) return;
      native = el;
      el.addEventListener('keydown', handleKeyDown);
    });

    return () => {
      cancelled = true;
      native?.removeEventListener('keydown', handleKeyDown);
    };
  }, [segment]);

  const handleFormat = async (action: MarkdownAction) => {
    const native = await textareaRef.current?.getInputElement();
    if (!native) return;
    const { text, selectionStart, selectionEnd } = applyMarkdownFormat(
      value,
      native.selectionStart ?? value.length,
      native.selectionEnd ?? value.length,
      action
    );
    onChange(text);
    // onChange updates the controlled value on the next render; wait for that commit
    // (rAF fires before paint) before restoring focus and the caret/selection.
    requestAnimationFrame(() => {
      native.focus();
      native.setSelectionRange(selectionStart, selectionEnd);
    });
  };

  return (
    <div>
      <IonSegment
        value={segment}
        onIonChange={(e) => setSegment((e.detail.value as EditorSegment) || 'write')}
        disabled={disabled}
      >
        <IonSegmentButton value="write">
          <IonLabel>Escribir</IonLabel>
        </IonSegmentButton>
        <IonSegmentButton value="preview">
          <IonLabel>Vista previa</IonLabel>
        </IonSegmentButton>
      </IonSegment>

      <div className="mt-3">
        {segment === 'write' ? (
          <>
            <div className="mb-2 flex items-center justify-between gap-2">
              <MarkdownToolbar onAction={handleFormat} disabled={disabled} />
              <MarkdownHelpButton />
            </div>
            <IonTextarea
              ref={textareaRef}
              fill="outline"
              autoGrow
              rows={4}
              counter
              maxlength={maxLength}
              disabled={disabled}
              value={value}
              placeholder={placeholder}
              onIonInput={(e) => onChange(e.detail.value || '')}
            />
          </>
        ) : (
          <div
            className={cn(
              'min-h-[6rem] rounded-md border border-gray-200 p-3',
              !value.trim() && 'flex items-center justify-center'
            )}
          >
            {value.trim() ? (
              <MarkdownContent content={value} />
            ) : (
              <span className="text-sm text-gray-400">Nada que previsualizar</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
